import React, { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const CoordinatorPanel = () => {
  const { toast } = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ role_requested: 'ashram_coord', entity_id: '', entity_type: 'ashram', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchMine(); }, []);

  const fetchMine = async () => {
    setLoading(true);
    try {
      const res = await api.get('/coordinator/my-requests');
      setRequests(res.data || []);
    } catch (e) { console.error('Failed to load coordinator requests:', e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/coordinator/request', {
        role_requested: form.role_requested,
        entity_type: form.role_requested === 'ashram_coord' ? 'ashram' : 'center',
        entity_id: form.entity_id || null,
        notes: form.notes || null,
      });
      toast({ title: 'Request submitted', description: 'Admin will review it shortly.' });
      setForm({ ...form, notes: '', entity_id: '' });
      fetchMine();
    } catch (err) {
      toast({ title: 'Submit failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="eyebrow mb-2">Coordinator Portal</div>
      <h1 className="font-serif-display text-4xl text-burgundy">Coordinator <span className="font-italic-display text-rose-italic">Access</span></h1>
      <p className="mt-2 text-mahogany/80 max-w-2xl">Request coordinator access to manage an ashram or center. All requests are reviewed by an admin.</p>

      <form onSubmit={handleSubmit} className="mt-8 grid gap-6 max-w-2xl">
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Role Type</span>
          <select value={form.role_requested} onChange={(e) => setForm({ ...form, role_requested: e.target.value })}
            className="w-full bg-transparent border-b border-mahogany/30 pb-3 pt-1 text-burgundy focus:outline-none focus:border-mahogany">
            <option value="ashram_coord">Ashram Coordinator</option>
            <option value="center_coord">Center Coordinator</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Ashram/Center ID (optional)</span>
          <input value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })}
            placeholder="Leave blank if new, or enter existing ID"
            className="w-full bg-transparent border-b border-mahogany/30 pb-3 pt-1 text-burgundy focus:outline-none focus:border-mahogany" />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Notes for Admin</span>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4}
            placeholder="Tell us about the ashram/center you’d like to coordinate…"
            className="w-full bg-transparent border border-mahogany/30 rounded-xl p-3 text-burgundy focus:outline-none focus:border-mahogany" />
        </label>
        <div>
          <button type="submit" disabled={submitting} className="pill-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit Request
          </button>
        </div>
      </form>

      <div className="mt-12">
        <h3 className="font-serif-display text-2xl text-burgundy">My Requests</h3>
        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : requests.length === 0 ? (
          <p className="mt-3 text-mahogany/70">You haven&apos;t submitted any requests yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {requests.map((r) => (
              <li key={r.id} className="p-4 rounded-xl border border-rose/50 bg-white/70">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-burgundy">{r.role_requested === 'ashram_coord' ? 'Ashram Coordinator' : 'Center Coordinator'}</div>
                    <div className="text-sm text-mahogany/70">Submitted {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full ${
                    r.status === 'approved' ? 'bg-green-100 text-green-800' :
                    r.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>{r.status}</span>
                </div>
                {r.notes && <p className="mt-2 text-sm text-mahogany/80">{r.notes}</p>}
                {r.review_notes && <p className="mt-2 text-sm text-red-700">Admin: {r.review_notes}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CoordinatorPanel;
